/**
 * Most of the block renderers; the form is in form.tsx, which is the only one
 * that holds state of its own, and the two that read ledger facts -- `thread`
 * and `graph` -- are in facts.tsx. A plugin ships a PanelSpec and nothing else;
 * these components are the whole of what the shell can draw (CHARTER §4).
 *
 * Every block reads one of the panel's `reads` by index and every action goes
 * back out through `ingress.submit` or `handle.complete`. There is no third
 * door.
 *
 * `PanelBlock.body` is the one "where to look" field the contract gives, and
 * every renderer that needs rows uses it the same way: a dotted path into the
 * source row. Nothing else in a block says where its data is.
 */

import { useMemo, useState } from "react";
import { MarkdownText } from "../components/MarkdownText";
import { LightMotion } from "../components/LightMotion";
import { AlertDialog } from "../components/rumonai";
import type { PanelAction, PanelBlock, PanelColumn } from "./spec";
import { pluck } from "./spec";
import type { SourceResult } from "../lib/api";
import { formatValue, formatTime, ledgerLabel, toneForState, TONE_GLYPH } from "../lib/format";
import { Clip, EmptyState, ErrorState, Scroller, StatusBadge } from "../shell/ui";
import { FormBlockView } from "./form";
import { GraphTree, ThreadLines } from "./facts";

/**
 * `id` is the caller's handle on "which button is busy right now".
 *
 * It answers with the obligation the core gave this press, or `""` when the
 * press created none and there is therefore nothing to follow. That is what
 * lets a form wait for the actual result instead of calling the request itself
 * a save (`formMachine.ts`, runtime §3): the id is the only thing a caller can
 * ask `/api/commands/<id>` about.
 */
export type RunAction = (
  action: PanelAction,
  values: Record<string, unknown>,
  id: string,
) => Promise<string>;

export interface BlockProps {
  block: PanelBlock;
  /** The panel's `reads`, in declaration order. A block picks one by index. */
  reads: SourceResult[];
  /** This block's position, which is the only name a block has now. */
  index: number;
  runAction: RunAction;
  busy: string | null;
}

/**
 * The rows a block draws.
 *
 * `body` is a dotted path into the source row -- that is how
 * `memory_timeline.status` puts four blocks on one tool read. Without it, the
 * source's own rows.
 */
function rowsOf(source: SourceResult | undefined, body?: string): Record<string, unknown>[] {
  if (!source) return [];
  const base = Array.isArray(source.rows)
    ? (source.rows as Record<string, unknown>[])
    : source.value && typeof source.value === "object" && !Array.isArray(source.value)
      ? [source.value as Record<string, unknown>]
      : [];
  if (!body) return base;
  const found = base.length ? pluck(base[0], body) : undefined;
  if (Array.isArray(found)) return found as Record<string, unknown>[];
  if (found && typeof found === "object") return [found as Record<string, unknown>];
  return [];
}

/**
 * A button whose label is a promise about what will happen when it is pressed.
 *
 * While the press is under way it says so in a word and shows the painted light, which the shared
 * motion stylesheet stills for reduced motion.  A plugin that has one action which is the point of the
 * panel marks it `metadata.primary`, and it is drawn as the page's main button.
 */
export function ActionButton({
  action,
  values,
  runAction,
  busy,
  id,
}: {
  action: PanelAction;
  values: Record<string, unknown>;
  /**
   * Looser than `RunAction` on purpose: a button needs the press to have
   * happened and nothing else. The form hands it a press that has already gone
   * through the state machine, and what that answers is the machine's business.
   */
  runAction: (action: PanelAction, values: Record<string, unknown>, id: string) => Promise<unknown>;
  busy: string | null;
  id: string;
}) {
  const [confirming, setConfirming] = useState(false);
  const [confirmingAction, setConfirmingAction] = useState(false);
  const running = busy === id || confirmingAction;
  const button = (
    <button
      type="button"
      className={action.metadata?.primary === true ? "btn btn-accent" : "btn"}
      disabled={running}
      aria-busy={running}
      onClick={() => {
        if (!action.confirm) void runAction(action, values, id).catch(() => {});
      }}
    >
      {running ? <><LightMotion compact state="working" />处理中</> : action.label}
    </button>
  );
  if (!action.confirm) return button;
  // The contract supplies a flag. The shell owns this single confirmation,
  // and keeps it open until the actual submission settles.
  return <AlertDialog open={confirming} onOpenChange={next => { if (!running) setConfirming(next); }}
    title={`确定${action.label}？`} description="这一步会真的执行。" confirmLabel={`确定${action.label}`}
    trigger={button} pending={running} onConfirm={() => {
      if (running) return;
      setConfirmingAction(true);
      void runAction(action, values, id).then(() => setConfirming(false))
        .catch(() => {}).finally(() => setConfirmingAction(false));
    }} />;
}

export function BlockShell({
  block,
  index,
  children,
}: {
  block: PanelBlock;
  index: number;
  children: React.ReactNode;
}) {
  return (
    <section className="block" data-block-kind={block.kind} data-block-index={index}>
      {block.title ? <h3 className="block-title">{block.title}</h3> : null}
      {children}
    </section>
  );
}

/**
 * What a card draws.
 *
 * A card's `body` may point at a scalar, and in the product it usually does:
 * `wechat.login` puts two cards on one read, one at `detail` and one at `url`,
 * both strings. Treating those as "no rows" is how a declared panel draws a
 * pair of 「这里现在是空的」 boxes over data it was handed.
 */
function cardsOf(source: SourceResult | undefined, body?: string): unknown[] {
  if (!source) return [];
  const base = rowsOf(source);
  if (!body) return base;
  const found = base.length ? pluck(base[0], body) : undefined;
  if (found === undefined || found === null || found === "") return [];
  return Array.isArray(found) ? found : [found];
}

function SourceGate<T>({
  block,
  reads,
  resolve,
  children,
}: {
  block: PanelBlock;
  reads: SourceResult[];
  resolve: (source: SourceResult | undefined, body?: string) => T[];
  children: (rows: T[]) => React.ReactNode;
}) {
  const at = block.source_index ?? 0;
  const source = reads[at];
  if (source?.state === "error") {
    return (
      <ErrorState
        error={{
          title: "数据没有拿到",
          detail: source.error ?? "核没有说原因。",
          hint: "面板的数据只能来自账本尾随和本插件的只读工具，没有第三条路。",
        }}
      />
    );
  }
  const rows = resolve(source, block.body);
  if (rows.length === 0) {
    // A block whose `source_index` addresses nothing is not an error: the core
    // allows index 0 with no reads, so this is what a panel that declares no
    // data looks like, and saying so is more use than a red box. The sentence
    // names the index the block asked for and how many there were -- it used to
    // report the block's own position and claim the panel declared no sources,
    // which is two wrong numbers when a panel has reads and a block overshoots.
    const hint = source
      ? (block.empty_text || "数据源返回了零行。等它有内容时这里会自己出现。")
      : reads.length === 0
        ? "这个面板没有声明数据源，所以这一块没有可画的东西。"
        : `这一块读第 ${at} 号数据源，而这个面板只声明了 ${reads.length} 个。`;
    return <EmptyState title="这里现在是空的" hint={hint} />;
  }
  return <>{children(rows)}</>;
}

/**
 * What a row hands to a button.
 *
 * `argument_fields` names keys, not paths -- so for a tail row, whose event
 * wraps the ledger row in an envelope, the row's own fields are lifted up
 * alongside it. A column still addresses `row.id` because that is where it is;
 * an action names `id` because that is what the ledger row calls it.
 */
function actionValues(row: Record<string, unknown>): Record<string, unknown> {
  const inner = row.row;
  return inner && typeof inner === "object" && !Array.isArray(inner)
    ? { ...row, ...(inner as Record<string, unknown>) }
    : row;
}

/**
 * The row's own keys, in the order it has them.
 *
 * `columns` is the table's field. A kv or card block has no way in the contract
 * to say which keys to show or what to call them, so these two draw everything
 * the value at `body` holds, under the plugin's own key names. Picking a subset
 * here would be the shell deciding what a plugin meant to publish, and giving
 * the keys prettier names would be the shell renaming another module's data.
 */
function entriesOf(row: Record<string, unknown>): PanelColumn[] {
  return Object.keys(row).map((key) => ({ key, label: key }));
}

function Cell({ column, row }: { column: PanelColumn; row: Record<string, unknown> }) {
  const raw = pluck(row, column.key);
  if (column.format === "status") {
    return <StatusBadge tone={toneForState(raw)} label={formatValue(raw, "status")} />;
  }
  const numeric = column.format === "number";
  return (
    <Clip
      text={formatValue(raw, column.format)}
      width={numeric ? "16ch" : undefined}
    />
  );
}

export function TableBlockView({ block, reads, index, runAction, busy }: BlockProps) {
  const columns = block.columns ?? [];
  const actions = block.actions ?? [];
  return (
    <BlockShell block={block} index={index}>
      <SourceGate block={block} reads={reads} resolve={rowsOf}>
        {(rows) => (
          <Scroller label={block.title || "表格"}>
            <table className="table">
              <caption className="visually-hidden">
                {block.title || `第 ${index + 1} 块`}，共 {rows.length} 行
              </caption>
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      scope="col"
                      className={col.format === "number" ? "num align-end" : undefined}
                      style={col.width ? { width: col.width } : undefined}
                    >
                      {col.label}
                    </th>
                  ))}
                  {actions.length ? <th scope="col">动作</th> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={String(pluck(row, "id") ?? i)}>
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={[
                          col.format === "number" ? "num align-end" : "",
                          col.format === "ref" ? "mono" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        <Cell column={col} row={row} />
                      </td>
                    ))}
                    {actions.length ? (
                      <td className="cell-actions">
                        {actions.map((action, j) => (
                          <ActionButton
                            key={action.key || j}
                            id={`${index}:${i}:${j}`}
                            action={action}
                            values={actionValues(row)}
                            runAction={runAction}
                            busy={busy}
                          />
                        ))}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </Scroller>
        )}
      </SourceGate>
    </BlockShell>
  );
}

/**
 * A timeline block is fed a tail source, so its rows are ledger events -- a
 * shape this shell already knows. That is why the contract gives the block no
 * keys to point at: there is nothing for a plugin to name.
 */
export function TimelineBlockView({ block, reads, index }: BlockProps) {
  return (
    <BlockShell block={block} index={index}>
      <SourceGate block={block} reads={reads} resolve={rowsOf}>
        {(rows) => (
          <ol className="tl">
            {rows.map((event, i) => {
              const row = (event.row ?? {}) as Record<string, unknown>;
              const state = row.state;
              return (
                <li key={String(event.cursor ?? i)} className="tl-item">
                  <span className="tl-glyph" aria-hidden="true">
                    {TONE_GLYPH[toneForState(state)]}
                  </span>
                  <time className="tl-time mono">{formatTime(event.ts)}</time>
                  <span className="tl-title">{ledgerLabel(String(event.ledger ?? ""))}</span>
                  <span className="tl-body">{String(event.kind ?? "")}</span>
                </li>
              );
            })}
          </ol>
        )}
      </SourceGate>
    </BlockShell>
  );
}

/**
 * A card is one value from a read, drawn as a block rather than as a table row.
 *
 * The value at `body` is the card. When it is a string -- which is what
 * `wechat.login` declares, one card at `detail` and one at `url` -- that string
 * is the whole of it. When it is an object, the card lists what the object
 * holds, first entry as the heading, because the contract gives a card no field
 * for choosing or renaming its keys.
 */
export function CardBlockView({ block, reads, index, runAction, busy }: BlockProps) {
  const actions = block.actions ?? [];
  return (
    <BlockShell block={block} index={index}>
      <SourceGate block={block} reads={reads} resolve={cardsOf}>
        {(items) => (
          <ul className="cards">
            {items.map((item, i) => {
              const row =
                item && typeof item === "object" && !Array.isArray(item)
                  ? (item as Record<string, unknown>)
                  : null;
              const [head, ...rest] = row ? entriesOf(row) : [];
              return (
                <li key={row ? String(pluck(row, "id") ?? i) : i} className={`card ${actions.length ? "pa-card" : "pa-open-group"}`}>
                  {/* Clipped to one line by `.card-title`, so the whole of it
                      has to be somewhere: a row's first field is the only thing
                      naming it, and half a file path names nothing. */}
                  <p
                    className="card-title"
                    title={
                      row
                        ? head
                          ? formatValue(pluck(row, head.key))
                          : ""
                        : formatValue(item)
                    }
                  >
                    {row
                      ? head
                        ? formatValue(pluck(row, head.key))
                        : ""
                      : formatValue(item)}
                  </p>
                  {rest.length && row ? (
                    <p className="card-meta micro">
                      {rest
                        .map((col) => formatValue(pluck(row, col.key)))
                        .filter((v) => v && v !== "未知")
                        .join(" · ")}
                    </p>
                  ) : null}
                  {actions.length ? (
                    <div className="card-actions">
                      {actions.map((action, j) => (
                        <ActionButton
                          key={action.key || j}
                          id={`${index}:${i}:${j}`}
                          action={action}
                          values={row ? actionValues(row) : {}}
                          runAction={runAction}
                          busy={busy}
                        />
                      ))}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </SourceGate>
    </BlockShell>
  );
}

/** A KV value that is a figure with its qualifier: `{value: "¥0.35", note: "问了 14 次模型"}`. */
function figureAndNote(raw: unknown): { value: string; note: string } | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const { value, note } = raw as { value?: unknown; note?: unknown };
  if (typeof value !== "string" && typeof value !== "number") return null;
  return { value: String(value), note: typeof note === "string" ? note : "" };
}

/**
 * KV draws the mapping at `body` as it is: every key it has, in the order it
 * has them. The contract gives no per-row labels here, and inventing ones would
 * mean the shell deciding what a plugin's own keys are called.
 */
export function KvBlockView({ block, reads, index }: BlockProps) {
  return (
    <BlockShell block={block} index={index}>
      <SourceGate block={block} reads={reads} resolve={rowsOf}>
        {(rows) => {
          const row = rows[0] ?? {};
          const columns = entriesOf(row);
          return (
            <dl className="kv pa-open-group">
              {columns.map((entry) => {
                const raw = pluck(row, entry.key);
                // A tile shows a number: the figure large, and what qualifies it as one short muted
                // line under it.  The plugin says both (`{value, note}`); the shell never splits a sentence.
                const tile = figureAndNote(raw);
                return (
                  <div className="kv-row" key={entry.key}>
                    <dt className="kv-key micro">{entry.label}</dt>
                    <dd className="kv-value num">
                      {tile ? tile.value : entry.format === "status" ? (
                        <StatusBadge tone={toneForState(raw)} label={formatValue(raw, "status")} />
                      ) : (
                        formatValue(raw, entry.format)
                      )}
                    </dd>
                    {tile?.note ? <dd className="kv-note micro">{tile.note}</dd> : null}
                  </div>
                );
              })}
            </dl>
          );
        }}
      </SourceGate>
    </BlockShell>
  );
}

/** Shared CommonMark / GFM renderer; panels and chat use the same rules. */
export function renderMarkdown(text: string): React.ReactNode {
  return <MarkdownText text={text} />;
}

/**
 * `body` is the text, or the path to it.
 *
 * The contract has one field for both (`PanelBlock.body`: "static body, or a
 * dotted path into the source row"), so the rule is: look it up; if the data
 * has it, that is the text, otherwise the body was the text all along.
 * One plugin writes a paragraph there and another writes `annotations`, and
 * both have to work. `body_path` is an explicit binding: missing data never
 * falls back to the path string and may deliberately leave no block yet.
 */
export function MarkdownBlockView({ block, reads, index }: BlockProps) {
  const source = reads[block.source_index ?? 0];
  const body = block.body ?? "";
  const binding = block.body_path ?? "";
  const text = useMemo(() => {
    const rows = rowsOf(source);
    const found = rows.length ? pluck(rows[0], binding || body) : undefined;
    if (typeof found === "string") return found;
    if (Array.isArray(found)) return found.map((v) => `- ${String(v)}`).join("\n");
    return binding ? "" : body;
  }, [body, binding, source]);

  if (source?.state === "error") {
    return (
      <BlockShell block={block} index={index}>
        <ErrorState error={{ title: "正文没有拿到", detail: source.error ?? "", hint: null }} />
      </BlockShell>
    );
  }
  if (!text) {
    if (binding && !block.empty_text) return null;
    return (
      <BlockShell block={block} index={index}>
        <EmptyState title="没有正文" hint={block.empty_text || "数据源里没有这段文字。"} />
      </BlockShell>
    );
  }
  return (
    <BlockShell block={block} index={index}>
      <div className="prose">{renderMarkdown(text)}</div>
    </BlockShell>
  );
}

/**
 * A conversation, read off the ledger (§7.130 phase 2).
 *
 * Like the timeline block it is fed a tail source and reads nothing the plugin
 * has to name -- what the plugin chooses is the filter. The rendering lives in
 * facts.tsx; what is here is the same source gate every other block goes
 * through, so an errored source says the same thing in all of them.
 */
export function ThreadBlockView({ block, reads, index }: BlockProps) {
  return (
    <BlockShell block={block} index={index}>
      <SourceGate block={block} reads={reads} resolve={rowsOf}>
        {(rows) => <ThreadLines block={block} rows={rows} />}
      </SourceGate>
    </BlockShell>
  );
}

/** Who delegated what to whom, off `child_spawned` (§7.130 phase 2). */
export function GraphBlockView({ block, reads, index }: BlockProps) {
  return (
    <BlockShell block={block} index={index}>
      <SourceGate block={block} reads={reads} resolve={rowsOf}>
        {(rows) => <GraphTree block={block} rows={rows} />}
      </SourceGate>
    </BlockShell>
  );
}

const RENDERERS: Record<string, (props: BlockProps) => React.ReactElement | null> = {
  table: TableBlockView,
  timeline: TimelineBlockView,
  card: CardBlockView,
  kv: KvBlockView,
  markdown: MarkdownBlockView,
  form: FormBlockView,
  thread: ThreadBlockView,
  graph: GraphBlockView,
};

export function BlockView(props: BlockProps) {
  const Renderer = RENDERERS[props.block.kind];
  if (!Renderer) {
    return (
      <ErrorState
        error={{
          title: "画不出这个 block",
          detail: `声明里写的是 ${props.block.kind}，壳画得出来的是这八种：表 · 时间线 · 表单 · 卡片 · Markdown · KV · 对话线 · 任务图。`,
          hint: "surface 由插件自己画，走 PanelView 的沙箱，不到这里。别的一律改插件的声明。",
        }}
      />
    );
  }
  return <Renderer {...props} />;
}
